import { DisruptionsAreaRoutes } from '@backoffice-frontend/common'
import {
  TracingAreaRoutes,
  VEHICLE_TRACING_RIGHTS,
} from '@backoffice-frontend/tracing-api'
import { VehicleAreaRoutes } from '@backoffice-frontend/vehicle-api'
import { BoumRight } from '@moia-dev/moia-token-claims'
import { matchPath } from 'react-router'

export type MinervaNavigationArea = 'tracing' | 'vehicle' | 'disruption'

export type MinervaNavigation = {
  area: MinervaNavigationArea
  link: string
}

type MinervaNavigationTarget = {
  /** The one route pattern a link may open; the model's link is never trusted beyond it. */
  route: string
  allowedRights: BoumRight[]
}

// Adding an area here is all a new chip needs, apart from its label and icon
// in MinervaNavigationButton. The backend gates the link too; the rights here
// keep a chip hidden from a user who could not open the page.
const MINERVA_NAVIGATION_TARGETS: Record<
  MinervaNavigationArea,
  MinervaNavigationTarget
> = {
  tracing: {
    route: TracingAreaRoutes.Vehicle.route,
    allowedRights: VEHICLE_TRACING_RIGHTS,
  },
  vehicle: {
    route: VehicleAreaRoutes.Overview.route,
    allowedRights: [BoumRight.AREA_VEHICLE],
  },
  disruption: {
    route: DisruptionsAreaRoutes.ViewReport.route,
    allowedRights: [BoumRight.REPORT_DISRUPTION_AREA],
  },
}

const isMinervaNavigationArea = (area: string): area is MinervaNavigationArea =>
  Object.hasOwn(MINERVA_NAVIGATION_TARGETS, area)

/**
 * The navigation item of an answer, or undefined when the model proposed none
 * or one the chat cannot open: an unknown area, or a link to any path but the
 * area's route (another page, another origin, a `//host` path).
 */
export const parseMinervaNavigation = (
  item: { area: string; link: string } | null | undefined,
): MinervaNavigation | undefined => {
  if (!item) return undefined
  const area = item.area.trim().toLowerCase()
  if (!isMinervaNavigationArea(area)) return undefined
  const { route } = MINERVA_NAVIGATION_TARGETS[area]
  const trimmed = item.link.trim()
  // Without the leading slash the router would resolve it from the dashboard.
  const link = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  const path = link.split(/[?#]/, 1)[0]
  // The URL parser rewrites `..`, `%2e%2e` and `//host`: a path it changes is not the one checked below.
  if (new URL(path, 'http://localhost').pathname !== path) return undefined
  if (!matchPath({ path: route, caseSensitive: true }, path)) return undefined
  return { area, link }
}

export const getMinervaNavigationRights = (
  area: MinervaNavigationArea,
): BoumRight[] => MINERVA_NAVIGATION_TARGETS[area].allowedRights
